<script setup lang="ts">
  import { ref, watch, nextTick } from "vue"

  defineOptions({ name: "AppTextarea" })

  const props = withDefaults(
    defineProps<{
      /** Textarea value (v-model) */
      modelValue?: string
      /** Placeholder text */
      placeholder?: string
      /** Number of visible text rows */
      rows?: number
      /** Maximum character length */
      maxlength?: number
      /** Autofocus on mount */
      autofocus?: boolean
      /** Mark as required */
      required?: boolean
      /** Disabled state */
      disabled?: boolean
      /** Error state (shows red border) */
      error?: boolean
      /** Auto-resize to fit content */
      autoresize?: boolean
      /** Name attribute */
      name?: string
      /** ID attribute */
      id?: string
    }>(),
    {
      modelValue: "",
      placeholder: undefined,
      rows: 3,
      maxlength: undefined,
      autofocus: false,
      required: false,
      disabled: false,
      error: false,
      autoresize: false,
      name: undefined,
      id: undefined,
    },
  )

  const emit = defineEmits<{
    (e: "update:modelValue", value: string): void
  }>()

  const textareaRef = ref<HTMLTextAreaElement | null>(null)

  const handleInput = (event: Event) => {
    const target = event.target as HTMLTextAreaElement
    emit("update:modelValue", target.value)

    if (props.autoresize) {
      resizeTextarea()
    }
  }

  const resizeTextarea = () => {
    if (!textareaRef.value) return

    // Reset height to auto to get the correct scrollHeight
    textareaRef.value.style.height = "auto"
    // Set height to scrollHeight to fit content
    textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`
  }

  // Watch for external value changes when autoresize is enabled
  watch(
    () => props.modelValue,
    () => {
      if (props.autoresize) {
        nextTick(resizeTextarea)
      }
    },
  )
</script>

<template>
  <div class="app-textarea" :class="{ 'app-textarea--error': error }">
    <textarea
      :id="id"
      ref="textareaRef"
      :value="modelValue"
      :placeholder="placeholder"
      :rows="autoresize ? 1 : rows"
      :maxlength="maxlength"
      :autofocus="autofocus"
      :required="required"
      :disabled="disabled"
      :name="name"
      class="app-textarea__field"
      @input="handleInput"
    />
  </div>
</template>

<style scoped>
  .app-textarea {
    display: flex;
    background: var(--app-textarea-bg, #0f172a);
    border: 1px solid var(--app-textarea-border, rgba(255, 255, 255, 0.15));
    border-radius: var(--app-textarea-radius, 0.375rem);
    padding: 0.5rem 0.75rem;
    transition: all 0.2s;
  }

  .app-textarea:focus-within {
    border-color: var(--app-textarea-focus-border, rgba(96, 165, 250, 0.5));
    background: var(--app-textarea-focus-bg, #0f172a);
  }

  .app-textarea--error {
    border-color: var(--app-textarea-error-border, rgba(239, 68, 68, 0.6));
  }

  .app-textarea--error:focus-within {
    border-color: var(
      --app-textarea-error-focus-border,
      rgba(239, 68, 68, 0.8)
    );
  }

  .app-textarea__field {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: var(--app-textarea-color, #fff);
    font-size: inherit;
    font-family: inherit;
    width: 100%;
    resize: vertical;
    min-height: 3rem;
  }

  .app-textarea__field::placeholder {
    color: var(--app-textarea-placeholder, rgba(255, 255, 255, 0.4));
  }

  .app-textarea__field:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* When autoresize is enabled, prevent manual resize */
  .app-textarea__field[rows="1"] {
    resize: none;
    overflow: hidden;
  }
</style>
